import { useEffect, useRef, useState } from "react";

type TurnstileWidgetId = string;

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      action?: string;
      appearance: "interaction-only";
      size: "flexible";
      callback: (token: string) => void;
      "error-callback": () => void;
      "expired-callback": () => void;
      "timeout-callback": () => void;
    },
  ) => TurnstileWidgetId;
  remove: (widgetId: TurnstileWidgetId) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstilePromise: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstilePromise) return turnstilePromise;

  turnstilePromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-conlit-turnstile]");
    const script = existing ?? document.createElement("script");
    const handleLoad = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("TURNSTILE_UNAVAILABLE"));
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", () => reject(new Error("TURNSTILE_LOAD_FAILED")), {
      once: true,
    });
    if (!existing) {
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.conlitTurnstile = "true";
      document.head.append(script);
    }
  });

  return turnstilePromise;
}

export function TurnstileChallenge({
  action,
  resetKey = 0,
  onToken,
  loadingLabel,
  errorLabel,
}: {
  action: "staff_login" | "student_join";
  resetKey?: number;
  onToken: (token: string | null) => void;
  loadingLabel: string;
  errorLabel: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error" | "bypassed">("loading");
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    let active = true;
    let api: TurnstileApi | undefined;
    let widgetId: TurnstileWidgetId | undefined;
    onToken(null);

    if (!siteKey) {
      if (import.meta.env.DEV || import.meta.env.MODE === "test") {
        setStatus("bypassed");
        onToken("local-development-bypass");
      } else {
        setStatus("error");
      }
      return;
    }

    setStatus("loading");
    void loadTurnstile()
      .then((loadedApi) => {
        if (!active || !container.current) return;
        api = loadedApi;
        widgetId = loadedApi.render(container.current, {
          sitekey: siteKey,
          action,
          appearance: "interaction-only",
          size: "flexible",
          callback: (token) => {
            if (!active) return;
            setStatus("ready");
            onToken(token);
          },
          "error-callback": () => {
            if (!active) return;
            setStatus("error");
            onToken(null);
          },
          "expired-callback": () => {
            if (!active) return;
            setStatus("loading");
            onToken(null);
          },
          "timeout-callback": () => {
            if (!active) return;
            setStatus("loading");
            onToken(null);
          },
        });
      })
      .catch(() => {
        if (!active) return;
        setStatus("error");
        onToken(null);
      });

    return () => {
      active = false;
      if (api && widgetId) api.remove(widgetId);
    };
  }, [action, onToken, resetKey, siteKey]);

  return (
    <div className="space-y-2">
      <div ref={container} className="min-h-16 w-full" />
      <p className="text-text-muted text-sm" aria-live="polite">
        {status === "loading" ? loadingLabel : status === "error" ? errorLabel : ""}
      </p>
    </div>
  );
}
